import type { CSSProperties, ReactNode } from 'react';
import { deltaColor } from './DeltaValue';

/**
 * Phase 25 — discriminated union for cost-ledger rows.
 *
 * The cost ledger renders four kinds of rows:
 *   - numeric:    side-by-side dollar values + computed Δ ($) + Δ (%)
 *   - totals:     same shape, but bold + lineSoft background
 *   - count:      raw integer counts (vehicle count); Δ is just b - a
 *   - string-mix: descriptive strings (fuel-type mix); no delta
 *
 * Each variant has its own discriminator for clarity at the call site.
 */
export type LedgerRowVariant =
  | {
      kind: 'numeric' | 'totals';
      label: ReactNode;
      labelTrailing?: ReactNode; // optional ⓘ icon next to label
      a: number;
      b: number;
      aSubtext?: ReactNode;
      bSubtext?: ReactNode;
      deltaSubtext?: ReactNode;
      /** "lower better" by default; pass true for credit / residual rows. */
      higherBetter?: boolean;
      /**
       * Pre-formatted dollar value. Default formats as ` $ ###`. Override
       * for credit rows that need explicit "−" prefix.
       */
      formatA?: string;
      formatB?: string;
      formatDelta?: string;
      formatPct?: string;
      /** Pre-computed winner for color rules (a / b / tie). */
      winner?: 'a' | 'b' | 'tie' | null;
    }
  | {
      kind: 'count';
      label: ReactNode;
      a: number;
      b: number;
    }
  | {
      kind: 'string-mix';
      label: ReactNode;
      a: ReactNode;
      b: ReactNode;
    }
  | {
      kind: 'per-mile';
      label: ReactNode;
      a: number;
      b: number;
    };

interface LedgerRowProps {
  row: LedgerRowVariant;
  /** Hairline above? First row should pass false to suppress the top divider. */
  hairlineTop?: boolean;
  /** Highlight class when |Δ| > 5%. */
  highlight?: boolean;
  /** Grid template — caller controls column widths to keep them aligned. */
  gridTemplateColumns: string;
}

const fmtUSD = (n: number): string => {
  if (!isFinite(n)) return '—';
  const abs = Math.abs(Math.round(n));
  return (n < 0 ? '−$' : '$') + abs.toLocaleString('en-US');
};

const fmtSignedUSD = (n: number): string => {
  if (!isFinite(n)) return '—';
  const abs = Math.abs(Math.round(n));
  if (n > 0) return '+$' + abs.toLocaleString('en-US');
  if (n < 0) return '−$' + abs.toLocaleString('en-US');
  return '$0';
};

const fmtPctSigned = (pct: number): string => {
  if (!isFinite(pct)) return '—';
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
  return sign + Math.abs(pct).toFixed(1) + '%';
};

const fmtPerMile = (n: number): string => '$' + n.toFixed(3);

const cellPadding = '13px 18px';

export default function LedgerRow({
  row,
  hairlineTop = true,
  highlight = false,
  gridTemplateColumns,
}: LedgerRowProps) {
  const isTotals = row.kind === 'totals';
  const baseRowStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns,
    borderTop: hairlineTop ? '1px solid var(--tco-line-soft)' : 'none',
    background: isTotals
      ? 'var(--tco-line-soft)'
      : highlight
        ? 'var(--tco-neg-soft)'
        : 'transparent',
    fontWeight: isTotals ? 600 : 400,
  };

  const labelCell: CSSProperties = {
    padding: cellPadding,
    color: 'var(--tco-ink)',
    fontSize: 14,
  };
  const numCell: CSSProperties = {
    padding: cellPadding,
    fontSize: 14,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: "'tnum'",
  };
  const subnoteStyle: CSSProperties = {
    fontSize: 10,
    color: 'var(--tco-ink-3)',
    fontWeight: 400,
    marginTop: 2,
  };

  if (row.kind === 'string-mix') {
    return (
      <div style={baseRowStyle}>
        <div style={labelCell}>{row.label}</div>
        <div style={{ ...numCell, color: 'var(--tco-ink)' }}>{row.a}</div>
        <div style={{ ...numCell, color: 'var(--tco-ink)' }}>{row.b}</div>
        <div style={{ ...numCell, color: 'var(--tco-ink-3)' }}>—</div>
        <div style={{ ...numCell, color: 'var(--tco-ink-3)' }}>—</div>
      </div>
    );
  }

  if (row.kind === 'count') {
    const delta = row.b - row.a;
    return (
      <div style={baseRowStyle}>
        <div style={labelCell}>{row.label}</div>
        <div style={{ ...numCell, color: 'var(--tco-ink)' }}>{row.a}</div>
        <div style={{ ...numCell, color: 'var(--tco-ink)' }}>{row.b}</div>
        <div
          style={{
            ...numCell,
            color: deltaColor(delta, { isCredit: false, winner: null }),
          }}
        >
          {delta > 0 ? '+' : ''}
          {delta}
        </div>
        <div style={{ ...numCell, color: 'var(--tco-ink-3)' }}>—</div>
      </div>
    );
  }

  if (row.kind === 'per-mile') {
    const delta = row.b - row.a;
    const pct = row.a !== 0 ? (delta / Math.abs(row.a)) * 100 : 0;
    const winner: 'a' | 'b' | 'tie' = Math.abs(delta) < 0.001 ? 'tie' : delta > 0 ? 'a' : 'b';
    // Phase 24.2: Scenario A/B value cells render neutral; only the Δ
    // and Δ% columns carry winner coloring.
    return (
      <div style={baseRowStyle}>
        <div style={labelCell}>{row.label}</div>
        <div style={{ ...numCell, color: 'var(--tco-ink)' }}>
          {fmtPerMile(row.a)}
        </div>
        <div style={{ ...numCell, color: 'var(--tco-ink)' }}>
          {fmtPerMile(row.b)}
        </div>
        <div
          style={{
            ...numCell,
            color: deltaColor(delta, { winner }),
          }}
        >
          {Math.abs(delta) < 0.001 ? '—' : (delta > 0 ? '+' : '−') + '$' + Math.abs(delta).toFixed(3)}
        </div>
        <div
          style={{
            ...numCell,
            color: deltaColor(pct, { winner }),
          }}
        >
          {Math.abs(pct) < 0.05 ? '—' : fmtPctSigned(pct)}
        </div>
      </div>
    );
  }

  // numeric / totals
  const delta = row.b - row.a;
  const pct = row.a !== 0 ? (delta / Math.abs(row.a)) * 100 : 0;
  const isCredit = !!row.higherBetter;
  // If winner not provided, derive it from direction-of-good.
  let winner: 'a' | 'b' | 'tie' | null = row.winner ?? null;
  if (!winner) {
    if (Math.abs(delta) < 1) {
      winner = 'tie';
    } else if (isCredit) {
      winner = row.a > row.b ? 'a' : 'b';
    } else {
      winner = row.a < row.b ? 'a' : 'b';
    }
  }
  // Phase 24.2: per the side-by-side ledger color convention, the Scenario
  // A and Scenario B value cells render neutral. Winner coloring (green for
  // the winner / red for the loser) is reserved for the Δ and Δ% columns.
  const aColor = 'var(--tco-ink)';
  const bColor = 'var(--tco-ink)';
  const dColor = deltaColor(delta, { isCredit, winner });
  const pColor = deltaColor(pct, { isCredit, winner });
  const aText = row.formatA ?? (isCredit && row.a > 0 ? '−' + fmtUSD(row.a) : fmtUSD(row.a));
  const bText = row.formatB ?? (isCredit && row.b > 0 ? '−' + fmtUSD(row.b) : fmtUSD(row.b));
  const dText = row.formatDelta ?? (Math.abs(delta) < 1 ? '$0' : fmtSignedUSD(delta));
  const pText = row.formatPct ?? (Math.abs(pct) < 0.05 ? '0.0%' : fmtPctSigned(pct));

  return (
    <div style={baseRowStyle}>
      <div style={labelCell}>
        {row.label}
        {row.labelTrailing}
      </div>
      <div style={{ ...numCell, color: aColor }}>
        <div>{aText}</div>
        {row.aSubtext && <div style={subnoteStyle}>{row.aSubtext}</div>}
      </div>
      <div style={{ ...numCell, color: bColor }}>
        <div>{bText}</div>
        {row.bSubtext && <div style={subnoteStyle}>{row.bSubtext}</div>}
      </div>
      <div style={{ ...numCell, color: dColor }}>
        <div>{dText}</div>
        {row.deltaSubtext && <div style={subnoteStyle}>{row.deltaSubtext}</div>}
      </div>
      <div style={{ ...numCell, color: pColor }}>{pText}</div>
    </div>
  );
}
